/**
 * control-group.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import {
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    Output
} from "@angular/core";
import { Modal } from "angular2-modal/plugins/bootstrap";

import { AnswerProxy } from "../../algorithms/management/answer-proxy";
import { BuildFlags } from "../../configuration/build-flags";
import { ValidationMessages }
    from "../../support/controls/validation-messages.component";
import { ModalHelpers, SLOW_REMOVAL, oneline } from "../../support/utilities";
import { ControlGroup, TabularControlGroup } from "./control-group";
import { ControlTableModalComponent } from "./control-table-modal.component";
import { TypedDataHolder, TypedDataHolderWithDefaults, TypedData } from "./typed-data";

const MULTI_ANSWER_ENABLED : boolean = false;

declare const require : (file : string) => string;

@Component({
    selector: `control-group`,
    template: require("./control-group.template.html"),
    styles: [`
    .intake-step, .tabular {
      position: relative;
    }
    .hover-helper {
      position: absolute;
      height: 100%;
      width: 100px;
      top: 0px;
      left: -100px;
      background: transparent;
    }
    .menu-button {
      display: block;
      position: absolute;
      left: -50px;
      top: 0;
      width: 20px;
      margin: 10px;
      text-align: center;
      opacity: 0;
      will-change: opacity;
      transition: opacity 0.4s;
    }
    .has-comment .glyphicon-comment {
      color: #3c763d; /* text-success color TODO: non-magic ref? */
    }
    .intake-step:hover .menu-button,
    .tabular:hover .menu-button,
    .has-comment .menu-button {
      opacity: 1;
    }
    /deep/ .hide-comments .intake-step:hover .menu-button,
    /deep/ .hide-comments .tabular:hover .menu-button,
    /deep/ .hide-comments .has-comment .menu-button {
        display: none;
    }
    .menu {
      max-height: 0px;
      opacity: 0;
      will-change: opacity, max-height, height;
      overflow-y: hidden;
      transition: max-height 0.8s, opacity 0.8s;
      padding-top: 5px;
      padding-left: 1em;
      border-left: 1px solid gray;
    }
    .menu.open {
      max-height: 1600px;
      opacity: 1;
    }
    .faded {
      /* background-color: #00FF55; */
      /* debugging, this is now handled by animation trigger */
    }
    `,
    `
    .tabular caption {
        caption-side: bottom;
        text-align: center;
    }
    .tabular table {
        overflow-x: scroll;
        width: 100%;
    }
    .tabular table.expanded {
        /*
        Absorb the intake form's left margin.
        */
        margin-left: -50px;
        width: calc(100% + 50px);
    }
    .tabular .grid td {
        /* border: 1px solid rgba(0,0,0,0.2); */
        /* max-width: 200px; */
        vertical-align: top;
        padding: 1px;
    }
    .tabular th {
        position: relative;
    }
    .tabular .grid .popover {
        display: block;
        top: 1.3em;
        left: -0.5em;
        min-width: 160px;
        font-weight: normal;
    }
    .tabular .collated .popover {
        display: block;
        top: -50%;
        left: 90%;
        font-weight: normal;
        width: 250px;
    }
    .collated {
        margin-top: 2em;
    }
    .collated > h3 {
        margin-top: 0;
    }
    .collated > table {
        /* margin-top: 10px; */
    }
    .tabular tfoot .alert {
        padding: 3px;
    }
    .tabular .grid >>> textarea {
        padding: 3px 5px;
        max-height: 80px;
    }
    .tabular .grid >>> .row-actions {
        min-width: 84px;
    }
    .tabular .grid >>> .row-actions .btn {
        padding: 6px;
    }
    .inline-report {
        padding-left: 12px;
        border-radius: 5px;
        border-left: 2px solid rgba(50, 150, 0, 0.25);
    }
    /* make table margin-left 8vw  */
    `],
    animations: [ SLOW_REMOVAL ]
})
export class ControlGroupComponent {
      public debugTools : boolean = BuildFlags.debugMode;

      public validateInColumn : boolean = false;

    @Input()
    public group : ControlGroup;

    public asDefault () : ControlGroup
    {
        return Object.assign({}, this.group, {type: null});
    }

    @Output()
    public action : EventEmitter<[string, any]> =
        new EventEmitter<[string, any]>();

    @Input()
    public rowActions : boolean = true;

    constructor (public modal : Modal, public answer : AnswerProxy)
    {
        // this.sortLabel
    }

    public act (name : string, args? : any) : void
    {
        if (name === "removeRow")
        {
            return this.removeRow(args[0]);
        }
        if (name === "moveRow")
        {
            return this.moveRow(args[0], args[1]);
        }
        this.action.emit([name, args]);
    }

    public change (event : Event, step : TypedDataHolder) : void
    {
        if (this.group instanceof TabularControlGroup)
        {
            this.group.autosize(true);
        }
        if (this.group instanceof TabularControlGroup && MULTI_ANSWER_ENABLED)
        {
            throw new Error(`Multi-answer support is not available at present`);
            // if (ValidationMessages.submittable(this.group.form))
            // {
            //     this.answer.submitMultiple(this.group.steps,  [event]);
            // }
        }
        else
        {
            this.answer.submitAnswer(step,  event);
        }
    }

    public openTable () : void
    {
        ModalHelpers.openCustom(
            this.modal,
            ControlTableModalComponent,
            {
                group: this.group,
                tunnel: (e : Event, s : TypedDataHolder) => this.change(e, s),
                act: (name : string, args? : any) => this.act(name, args),
                dialogClass: "modal-dialog modal-full"
            }
        );
    }

    public removeRow (row : number) : void
    {
        // debugger;
        if (this.group instanceof TabularControlGroup)
        {
            this.group.remove(row);
        }
        else
        {
            throw new Error(`Cannot remove for non-tabular groups!`);
        }
        this.answer.submitMultiple(this.group.steps, []);
    }

    public moveRow (row : number, delta : number) : void
    {
        // debugger;
        if (this.group instanceof TabularControlGroup)
        {
            this.group.move(row, delta);
        }
        else
        {
            throw new Error(`Cannot move for non-tabular groups!`);
        }
        this.answer.submitMultiple(this.group.steps, []);
    }

    public clearAll () : void
    {
        // debugger;
        if (this.group instanceof TabularControlGroup)
        {
            try {
                do {
                    this.group.remove(0);
                }
                while (true);
            }
            catch (err)
            {
                //
            }
        }
        else
        {
            throw new Error(`Cannot move for non-tabular groups!`);
        }
        this.answer.submitMultiple(this.group.steps, []);
    }

    private cachedSortLabel : string | null = null;
    private cachedSortStep : TypedDataHolder | null = null;

    // public sortLabel () : string | null
    // {
    //     if (this.cachedSortLabel !== null)
    //     {
    //         return this.cachedSortLabel;
    //     }
    //     this.group.steps.forEach((s : any) => {
    //         if (s.hints && s.hints.tableSorter)
    //         {
    //             this.cachedSortStep = s;
    //             this.cachedSortLabel = s.hints.tableSorter;
    //             return this.cachedSortLabel;
    //         }
    //     });
    //     return null;
    // }

    public sort (stepName : string) : void
    {
        console.error("SORTING", this.group);
        ModalHelpers.observeBooleanConfirmation(
            this.modal.confirm()
            .title("Sorting will overwrite current order")
            .okBtn("Sort table")
            .cancelBtn("Nevermind")
            .body(oneline`
                Sorting this table will sort it for all users (not just you).
                If you save the record, any order previously set for these items
                will be lost.`)
            .open()
        ).subscribe((goAhead : boolean) => {
            if (goAhead)
            {
                (this.group as TabularControlGroup).sortByName(stepName);
                this.answer.submitMultiple(this.group.steps, [new Event("sort")]);
            }
        });
    }

    public cellPercentage (double : boolean = false) : string
    {
        const total : number = (
            this.group.steps.length
            + this.group.steps.filter(
                (step : any) => step.hints && step.hints.multiline
            ).length
        );
        return `${Math.floor((double ? 2 : 1) * 100 / total)}%`;
    }

    public handleCustom ([name, event] : [string, Event])
    {
        if (name === 'fcwi-income-table'
            && event.target instanceof HTMLInputElement)
        {
            const value : number = Number.parseInt(event.target.value, 10);
            const stepName : string = event.target.id;
            if (Number.isSafeInteger(value) && value >= 0)
            {
                this.answer.submitRaw(stepName, value);
            }
        } else {
            console.error(
                `Unknown custom intake "${name}" -- events are not handled`);
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/common/data/control-group.component.ts